import { ECardinality, ESdChangeReason, ESymbolCategory, IScreenInput } from "@kuika/kuika-cl-model";
import { IScreenDesignContextState } from "../../contexts/screen-design-context/screen-design-context";
import type { ScreenDesignInstance } from "./designer-context";
import { getValues } from "./designer-context";
import { getScreenProvider } from "./designer-utils";

const CARDINALITY_MAP: Record<string, ECardinality> = {
  Unknown: ECardinality.Unknown,
  One: ECardinality.One,
  Many: ECardinality.Many,
  LikeFirstParam: ECardinality.LikeFirstParam,
  Error: ECardinality.Error,
  Unbound: ECardinality.Unbound,
  Any: ECardinality.Any
};

const CARDINALITY_LABELS: Record<number, string> = {
  [ECardinality.Unknown]: "Unknown",
  [ECardinality.One]: "One",
  [ECardinality.Many]: "Many",
  [ECardinality.LikeFirstParam]: "LikeFirstParam",
  [ECardinality.Error]: "Error",
  [ECardinality.Unbound]: "Unbound",
  [ECardinality.Any]: "Any"
};

interface SerializedScreenInput {
  id: string;
  name: string;
  type: string;
  cardinality: string | ECardinality;
  description?: string;
}

function getScreenInputs(sd: IScreenDesignContextState): IScreenInput[] {
  return getScreenProvider(sd)?.getScreenInputs?.() || [];
}

function resolveCardinality(value: string | number | undefined): ECardinality {
  if (value === undefined || value === null) return ECardinality.One;
  if (typeof value === "number") return value as ECardinality;
  return CARDINALITY_MAP[value] ?? ECardinality.One;
}

function serializeScreenInput(input: IScreenInput): SerializedScreenInput {
  return {
    ...input,
    cardinality: CARDINALITY_LABELS[input.cardinality] || input.cardinality
  };
}

export function listDesignerScreenInputs(sdInstance: ScreenDesignInstance): {
  count: number;
  screenInputs: SerializedScreenInput[];
} {
  const inputs: IScreenInput[] = getScreenInputs(getValues(sdInstance));
  return {
    count: inputs.length,
    screenInputs: inputs.map((input: IScreenInput) => ({
      id: input.id,
      name: input.name,
      type: input.type,
      cardinality: CARDINALITY_LABELS[input.cardinality] || input.cardinality,
      description: input.description || undefined
    }))
  };
}

interface FriendlyClash {
  readonly kuikaId: string;
  readonly componentName: string;
  readonly friendlyName: string;
}

/**
 * Fix #33 — Walk the current screen's component tree and collect every
 * DI whose `friendlyName` matches `candidate` (case-insensitive). LLMs
 * occasionally invent screen inputs that shadow real form fields (e.g.
 * a screen with a `Title` TextInput then a `Title` screen input). A
 * screen input is a value RECEIVED from a navigating caller — it is
 * never a stand-in for a form field on the same screen, and the
 * shadowing makes `screenInput:Title` and `di:<id>:value` ambiguous
 * to every downstream binder.
 */
function findFriendlyNameClashes(sd: IScreenDesignContextState, candidate: string): FriendlyClash[] {
  const dict: Record<number, unknown> = sd.screenDesignDictionary ?? {};
  const target: string = candidate.toLowerCase();
  const out: FriendlyClash[] = [];
  for (const key of Object.keys(dict)) {
    const di = dict[Number(key)] as { id?: number | string; friendlyName?: string; item?: { name?: string } };
    const friendlyName: string | undefined = di?.friendlyName ?? undefined;
    if (!friendlyName) continue;
    if (friendlyName.toLowerCase() !== target) continue;
    out.push({
      kuikaId: String(di.id ?? key),
      componentName: di.item?.name ?? "Unknown",
      friendlyName
    });
  }
  return out;
}

/** One requested screen input in a batch create. */
export interface ScreenInputCreateRequest {
  name?: string;
  type?: string;
  cardinality?: string | number;
  description?: string;
  id?: string;
}

/** Aggregate outcome of a batch create/delete — the shape the caller reports on. */
export interface ScreenInputBatchResult {
  ok: number;
  errors: number;
  total: number;
  errorMessages?: string[];
  /** KOA-18173 — components suffix-renamed to free a colliding screen-input name,
   *  as `"<old>" → "<new>" (kuikaId)`. Surfaced so the rename is never invisible. */
  renamedComponents?: string[];
  /**
   * Whether the write actually REACHED THE PLATFORM.
   *
   * `ok` counts what validated, which is not the same claim and was being read as
   * if it were. Screen inputs live in `publicMetaData` (the PublicJSON column) and
   * used to persist through an un-awaited `saveScreen`, so a batch could answer
   * `{ok: 1}` and be gone a second later — measured on session 25c5e8e8, four
   * screens, all four empty afterwards. Absent when nothing needed persisting.
   */
  persisted?: boolean;
}

/** One component whose friendlyName was moved aside to free a screen-input name. */
interface ComponentRename {
  readonly kuikaId: string;
  readonly from: string;
  readonly to: string;
}

/**
 * KOA-18173 — pick a free friendlyName for a component being moved aside. Prefers
 * `<name>Input`; on collision walks `<name>Input2`, `<name>Input3`, … Compared
 * case-insensitively against every existing friendlyName AND every declared screen
 * input, so the new name cannot re-create the shadowing it is resolving.
 */
function pickFreeComponentName(sd: IScreenDesignContextState, base: string): string {
  const dict: Record<number, unknown> = sd.screenDesignDictionary ?? {};
  const taken: Set<string> = new Set<string>();
  for (const key of Object.keys(dict)) {
    const di = dict[Number(key)] as { friendlyName?: string };
    const friendlyName: string | undefined = di?.friendlyName ?? undefined;
    if (friendlyName) taken.add(friendlyName.toLowerCase());
  }
  for (const screenInput of getScreenInputs(sd)) taken.add(screenInput.name.toLowerCase());

  const preferred: string = `${base}Input`;
  if (!taken.has(preferred.toLowerCase())) return preferred;
  for (let suffix = 2; suffix < 100; suffix++) {
    const candidate: string = `${preferred}${suffix}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
  return `${preferred}${crypto.randomUUID().slice(0, 6)}`;
}

/**
 * KOA-18173 — resolve a shadowing clash by moving the COMPONENT aside instead of
 * refusing the screen input.
 *
 * The screen input is the load-bearing contract: sibling screens' navigates pass
 * values into it and this screen's actions bind their identity from it
 * (`screenInput:<name>`). A component's friendlyName is incidental by comparison —
 * nothing binds to it that cannot be re-resolved by kuikaId.
 *
 * Failing closed here was measured to cost far more than the rename: on
 * `chat_1786432954662_qgkxex` the guard refused `WorkOrderId` on four screens, so
 * `GetWorkOrder.Id`, `ListPartsUsed.WorkOrderId`, `CreatePartUsed.WorkOrderId` and
 * `UpdatePartUsed.WorkOrderId` had nothing to bind to and DesignCodeValidation
 * blocked Preview on all of them.
 *
 * Mutates `di.friendlyName` in place on the shared screen-design tree; the caller's
 * `setScreenInputs` persist carries the rename with it. Returns the renames so the
 * caller can report them — a silent rename would be its own defect.
 */
function renameClashingComponents(sd: IScreenDesignContextState, clashes: readonly FriendlyClash[]): ComponentRename[] {
  const dict: Record<number, unknown> = sd.screenDesignDictionary ?? {};
  const applied: ComponentRename[] = [];
  for (const clash of clashes) {
    const di = dict[Number(clash.kuikaId)] as { friendlyName?: string } | undefined;
    if (!di) continue;
    const next: string = pickFreeComponentName(sd, clash.friendlyName);
    di.friendlyName = next;
    applied.push({ kuikaId: clash.kuikaId, from: clash.friendlyName, to: next });
  }
  return applied;
}

/** Render a rename list for the caller-visible report. */
function formatRenames(renames: readonly ComponentRename[]): string[] {
  return renames.map((r: ComponentRename): string => `"${r.from}" → "${r.to}" (kuikaId ${r.kuikaId})`);
}

/**
 * KOA-17362 — validate one requested input against the screen, WITHOUT saving.
 *
 * Split out of {@link createDesignerScreenInput} so a batch can run every check
 * and then persist ONCE. `takenNames` carries the names already accepted earlier
 * in the same batch, so two items requesting the same name still collide.
 */
function validateNewScreenInput(
  sd: IScreenDesignContextState,
  request: ScreenInputCreateRequest,
  takenNames: ReadonlySet<string>
): { error: string } | { ok: true; screenInput: IScreenInput; clashes: readonly FriendlyClash[] } {
  const { name, type, cardinality, description } = request;
  if (!name) return { error: "name is required" };
  if (!type) return { error: "type is required" };
  if (takenNames.has(name)) return { error: `Screen input "${name}" already exists` };

  // KOA-18173 — a friendlyName clash is REPORTED, not fatal. The caller moves the
  // colliding component aside (see renameClashingComponents) and creates the input,
  // because the input is the contract the rest of the build binds against. Refusing
  // it here left every `screenInput:<name>` consumer permanently unbindable.
  return {
    ok: true,
    clashes: findFriendlyNameClashes(sd, name),
    screenInput: {
      id: request.id || crypto.randomUUID(),
      name,
      type,
      cardinality: resolveCardinality(cardinality),
      description: description || ""
    }
  };
}

/**
 * KOA-17362 — create MANY screen inputs with a SINGLE persist.
 *
 * Every single-input create ends in `setScreenInputs`, which used to call
 * `saveScreen` from inside a `setState` callback and NOT await it (it now
 * resolves on the persist and reports it as `persisted`). The server-side batch
 * fan-out used to expand `{inputs: [a, b]}` into two sequential single creates, so
 * two saves went out back to back carrying `[a]` and `[a, b]`; whichever finished
 * LAST won, and when that was the first one `b` was simply gone. That is why live
 * smoke 13 lost the SECOND input of every batch — `ProcessInstanceId` survived and
 * `Order_Id` vanished, on all three screens, on every one of the three passes, and
 * a reconcile pass kept "restoring" it into the same race.
 *
 * Validating first and persisting once removes the race by construction (and costs
 * one round-trip instead of N). Per-item failures are reported without aborting the
 * rest, matching the fan-out contract callers already parse.
 *
 * AWAITS the persist and reports it as `persisted`. It used to return the moment the
 * in-memory patch was made, so `ok` was a count of what VALIDATED and every caller
 * read it as a count of what LANDED. On session 25c5e8e8 four screens answered
 * `{ok: 1}` and the platform holds none of those inputs: `setScreenInputs` patched
 * `publicMetaData`, `loadProject`'s late `setState({ screenList })` replaced the
 * container, and the next save wrote `screenInputs: []` over the top. A number that
 * cannot distinguish "written" from "about to be discarded" is not a result.
 */
export async function createDesignerScreenInputs(
  sdInstance: ScreenDesignInstance,
  requests: readonly ScreenInputCreateRequest[]
): Promise<ScreenInputBatchResult> {
  const sd: IScreenDesignContextState = getValues(sdInstance);
  const screenInputs: IScreenInput[] = getScreenInputs(sd);
  const takenNames: Set<string> = new Set<string>(screenInputs.map((si: IScreenInput) => si.name));

  const accepted: IScreenInput[] = [];
  const errorMessages: string[] = [];
  const renames: ComponentRename[] = [];
  for (const request of requests) {
    const outcome = validateNewScreenInput(sd, request, takenNames);
    if ("error" in outcome) {
      errorMessages.push(outcome.error);
      continue;
    }
    // Move colliding components aside only for an input we are actually going to
    // persist — the rename rides the same `setScreenInputs` save below, so a
    // rename without an accepted input would mutate the tree with nothing to commit.
    if (outcome.clashes.length > 0) renames.push(...renameClashingComponents(sd, outcome.clashes));
    accepted.push(outcome.screenInput);
    takenNames.add(outcome.screenInput.name);
  }

  let persisted: boolean | undefined;
  if (accepted.length > 0) {
    // ONE write, ONE save — no concurrent saves to lose each other's payload.
    persisted = await getScreenProvider(sd).setScreenInputs(
      [...screenInputs, ...accepted],
      ESdChangeReason.ChangeScreenInput
    );
  }

  const result: ScreenInputBatchResult = {
    ok: accepted.length,
    errors: errorMessages.length,
    total: requests.length,
    ...(persisted === undefined ? {} : { persisted })
  };
  const withRenames: ScreenInputBatchResult =
    renames.length > 0 ? { ...result, renamedComponents: formatRenames(renames) } : result;
  return errorMessages.length > 0 ? { ...withRenames, errorMessages } : withRenames;
}

export async function createDesignerScreenInput(
  sdInstance: ScreenDesignInstance,
  input: { name?: string; type?: string; cardinality?: string | number; description?: string; id?: string }
): Promise<
  { error: string } | { success: true; message: string; screenInput: SerializedScreenInput; persisted: boolean }
> {
  const { name, type, cardinality, description } = input;
  if (!name) return { error: "name is required" };
  if (!type) return { error: "type is required" };

  const sd: IScreenDesignContextState = getValues(sdInstance);
  const existing: IScreenInput[] = getScreenInputs(sd);
  if (existing.some((screenInput: IScreenInput) => screenInput.name === name)) {
    return { error: `Screen input "${name}" already exists` };
  }

  // Fix #33 — a screen input must never SHARE a name with a component on the same
  // screen: the shadowing makes `screenInput:Title` and `di:<TitleInput>:value`
  // ambiguous to every downstream binder (reproducer: the May 2026 fikir-havuzu
  // build, where the Binder "fixed" unbindable form fields by inventing screen
  // inputs of the same names).
  //
  // KOA-18173 — the ambiguity is resolved by moving the COMPONENT aside, not by
  // refusing the input. Refusing left every `screenInput:<name>` consumer with
  // nothing to bind to: on `chat_1786432954662_qgkxex` this guard rejected
  // `WorkOrderId` on four screens and Preview blocked on `GetWorkOrder.Id`,
  // `ListPartsUsed.WorkOrderId`, `CreatePartUsed.WorkOrderId` and
  // `UpdatePartUsed.WorkOrderId`. The rename is reported, never silent.
  const friendlyClashes: FriendlyClash[] = findFriendlyNameClashes(sd, name);
  const renames: ComponentRename[] = renameClashingComponents(sd, friendlyClashes);

  const newInput: IScreenInput = {
    id: input.id || crypto.randomUUID(),
    name,
    type,
    cardinality: resolveCardinality(cardinality),
    description: description || ""
  };

  const persisted: boolean = await getScreenProvider(sd).createScreenInput(newInput);
  const renameNote: string =
    renames.length > 0
      ? ` — renamed ${renames.length} shadowing component(s): ${formatRenames(renames).join(", ")}`
      : "";
  // The message says what was asked for; `persisted` says what the platform kept.
  const persistNote: string = persisted ? "" : " — NOT PERSISTED (the save was rejected or found no screen)";
  return {
    success: true,
    message: `Created screen input "${name}" (${type})${renameNote}${persistNote}`,
    screenInput: serializeScreenInput(newInput),
    persisted
  };
}

export async function updateDesignerScreenInput(
  sdInstance: ScreenDesignInstance,
  input: { id?: string; name?: string; type?: string; cardinality?: string | number }
): Promise<
  { error: string } | { success: true; message: string; screenInput: SerializedScreenInput; persisted: boolean }
> {
  const { id, name, type, cardinality } = input;
  if (!id) return { error: "id is required" };

  const sd: IScreenDesignContextState = getValues(sdInstance);
  const existing: IScreenInput[] = getScreenInputs(sd);
  const target: IScreenInput | undefined = existing.find((screenInput: IScreenInput) => screenInput.id === id);
  if (!target) return { error: `Screen input with id "${id}" not found` };
  if (name && name !== target.name && existing.some((screenInput: IScreenInput) => screenInput.name === name)) {
    return { error: `Screen input "${name}" already exists` };
  }

  const updated: IScreenInput = { ...target };
  if (name !== undefined) updated.name = name;
  if (type !== undefined) updated.type = type;
  if (cardinality !== undefined) updated.cardinality = resolveCardinality(cardinality);

  const persisted: boolean = await getScreenProvider(sd).updateScreenInput(updated, ESdChangeReason.ChangeScreenInput);
  const persistNote: string = persisted ? "" : " — NOT PERSISTED (the save was rejected or found no screen)";
  return {
    success: true,
    message: `Updated screen input "${updated.name}"${persistNote}`,
    screenInput: serializeScreenInput(updated),
    persisted
  };
}

/**
 * KOA-17362 — delete MANY screen inputs with a SINGLE persist, for the same
 * reason {@link createDesignerScreenInputs} exists: N un-awaited saves race and
 * the last one to land wins, so all but one deletion can silently come back.
 */
export async function deleteDesignerScreenInputs(
  sdInstance: ScreenDesignInstance,
  ids: readonly string[]
): Promise<ScreenInputBatchResult> {
  const sd: IScreenDesignContextState = getValues(sdInstance);
  const screenInputs: IScreenInput[] = getScreenInputs(sd);
  const errorMessages: string[] = [];
  const removeIds: Set<string> = new Set<string>();
  for (const id of ids) {
    if (!id) {
      errorMessages.push("id is required");
      continue;
    }
    const target: IScreenInput | undefined = screenInputs.find((si: IScreenInput) => si.id === id);
    if (!target) {
      errorMessages.push(`Screen input with id "${id}" not found`);
      continue;
    }
    removeIds.add(id);
  }

  let persisted: boolean | undefined;
  if (removeIds.size > 0) {
    persisted = await getScreenProvider(sd).setScreenInputs(
      screenInputs.filter((si: IScreenInput) => !removeIds.has(si.id)),
      ESdChangeReason.ChangeScreenInput
    );
  }

  const result: ScreenInputBatchResult = {
    ok: removeIds.size,
    errors: errorMessages.length,
    total: ids.length,
    // A deletion that does not persist comes BACK on the next load, which reads to
    // every caller as "the delete did nothing" — the same lie in the other direction.
    ...(persisted === undefined ? {} : { persisted })
  };
  return errorMessages.length > 0 ? { ...result, errorMessages } : result;
}

export async function deleteDesignerScreenInput(
  sdInstance: ScreenDesignInstance,
  id?: string
): Promise<{ error: string } | { success: true; message: string; persisted: boolean }> {
  if (!id) return { error: "id is required" };

  const sd: IScreenDesignContextState = getValues(sdInstance);
  const existing: IScreenInput[] = getScreenInputs(sd);
  const target: IScreenInput | undefined = existing.find((screenInput: IScreenInput) => screenInput.id === id);
  if (!target) return { error: `Screen input with id "${id}" not found` };

  const persisted: boolean = await getScreenProvider(sd).deleteScreenInput(target);
  const persistNote: string = persisted ? "" : " — NOT PERSISTED (the save was rejected or found no screen)";
  return {
    success: true,
    message: `Deleted screen input "${target.name}"${persistNote}`,
    persisted
  };
}

/** One declared screen input plus whether anything on the screen reads it. */
export interface ScreenInputUsage {
  readonly id: string;
  readonly name: string;
  /** True when some symbol or string on this screen references the input. */
  readonly referenced: boolean;
}

/**
 * KOA-18173 — which declared screen inputs anything on THIS screen actually reads.
 *
 * A screen input exists so the page can show the right record: its consumers are
 * action parameters, conditions, display bindings and filter variables, and they all
 * live on the declaring page. A navigate into the page is a SUPPLIER on a different
 * page, not a consumer — which is why supplier and consumer are pruned together.
 *
 * Complete by construction rather than surface-by-surface. Enumerating surfaces was
 * the wrong instinct: `IConditionModel.field1/field2` are `ISymbol` and nest
 * recursively through `conditionSet`, and `IAction.ifThenElseData` / `forEachData`
 * carry nested actions, so any hand-written walk would have missed a consumer and
 * deleted an input a condition reads. Instead this deep-walks the whole screen design
 * and treats ANY screen-input symbol — at any depth, in any container — as a
 * reference, plus any string that mentions the name in `screenInput` form.
 *
 * Deliberately over-reports: a false "referenced" costs nothing (the input simply
 * survives), while a false "unreferenced" would delete something the page needs.
 */
export function getDesignerScreenInputUsage(sdInstance: ScreenDesignInstance): {
  screenName: string;
  usage: ScreenInputUsage[];
} {
  const sd: IScreenDesignContextState = getValues(sdInstance);
  const declared: IScreenInput[] = getScreenInputs(sd);
  const design: unknown = getScreenProvider(sd)?.getScreenDesign?.();

  const referencedNames: Set<string> = new Set<string>();
  const stringHits: string[] = [];
  collectScreenInputReferences(design, referencedNames, stringHits, 0, new Set<unknown>());

  const usage: ScreenInputUsage[] = declared.map((input: IScreenInput): ScreenInputUsage => {
    const lower: string = input.name.toLowerCase();
    const bySymbol: boolean = referencedNames.has(lower);
    const byString: boolean = stringHits.some((text: string): boolean => text.includes(lower));
    return { id: input.id, name: input.name, referenced: bySymbol || byString };
  });

  const screenDesign = design as { name?: string } | undefined;
  return { screenName: screenDesign?.name ?? "", usage };
}

/** Guard against a pathological structure; the screen design is a tree in practice. */
const MAX_WALK_DEPTH: number = 40;

/**
 * Recursively collect screen-input references. Two channels:
 *  - a symbol whose category is `screenInput` contributes its `inputName`;
 *  - any string mentioning `screenInput` is kept verbatim so a name embedded in an
 *    expression (`screenInput.WorkOrderId`, a formula, a filter variable) still counts.
 */
function collectScreenInputReferences(
  node: unknown,
  names: Set<string>,
  stringHits: string[],
  depth: number,
  seen: Set<unknown>
): void {
  if (node === null || node === undefined || depth > MAX_WALK_DEPTH) return;
  if (typeof node === "string") {
    if (node.toLowerCase().includes("screeninput")) stringHits.push(node.toLowerCase());
    return;
  }
  if (typeof node !== "object") return;
  if (seen.has(node)) return;
  seen.add(node);

  if (Array.isArray(node)) {
    for (const item of node) collectScreenInputReferences(item, names, stringHits, depth + 1, seen);
    return;
  }

  const record = node as { category?: unknown; inputName?: unknown };
  if (record.category === ESymbolCategory.screenInput && typeof record.inputName === "string") {
    const trimmed: string = record.inputName.trim();
    if (trimmed.length > 0) names.add(trimmed.toLowerCase());
  }
  for (const value of Object.values(node as Record<string, unknown>)) {
    collectScreenInputReferences(value, names, stringHits, depth + 1, seen);
  }
}
